<template>
  <div class="home general">
    <h1 class="section-title">Horarios y repeticiones</h1>

    <div class="filters">
      <va-input v-model="since" type="date" label="Desde" />
      <va-input v-model="until" type="date" label="Hasta" />
      <va-button color="info" :disabled="isLoading" @click="loadReport">
        Consultar
      </va-button>
    </div>

    <p v-if="error" class="analytics-error">{{ error }}</p>
    <p v-if="isLoading" class="analytics-status">Cargando patrones...</p>

    <div v-if="report" class="summary">
      <div class="summary-item">
        <span>Reproducciones</span>
        <strong>{{ report.summary.plays }}</strong>
      </div>
      <div class="summary-item">
        <span>Hora pico</span>
        <strong>{{ report.summary.peak_hour_label || formatHour(report.summary.peak_hour) }}</strong>
      </div>
      <div class="summary-item">
        <span>Día pico</span>
        <strong>{{ report.summary.peak_weekday || "-" }}</strong>
      </div>
      <div class="summary-item">
        <span>Usuarios que vuelven</span>
        <strong>{{ formatPercent(report.summary.repeat_user_rate) }}</strong>
      </div>
      <div class="summary-item">
        <span>Canciones repetidas</span>
        <strong>{{ report.summary.repeat_song_pairs }}</strong>
      </div>
      <div class="summary-item">
        <span>Repros. por usuario</span>
        <strong>{{ formatNumber(report.summary.avg_plays_per_user) }}</strong>
      </div>
    </div>

    <h2 class="section-title">Por hora</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.hours : []"
      :columns="hourColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(share)="{ rowData }">
        {{ formatPercent(rowData.share) }}
      </template>
    </va-data-table>

    <h2 class="section-title">Por día</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.weekdays : []"
      :columns="weekdayColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(share)="{ rowData }">
        {{ formatPercent(rowData.share) }}
      </template>
    </va-data-table>

    <h2 class="section-title">Cuántas veces canta cada usuario</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.frequency_buckets : []"
      :columns="bucketColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    />

    <h2 class="section-title">Usuarios más frecuentes</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.user_frequency : []"
      :columns="userColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(plays_per_day)="{ rowData }">
        {{ formatNumber(rowData.plays_per_day) }}
      </template>
    </va-data-table>

    <h2 class="section-title">Canciones que más se repiten</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.repeated_songs : []"
      :columns="repeatSongColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(song_title)="{ rowData }">
        <router-link
          v-if="rowData.songId"
          class="table-span linked"
          :to="{ name: 'songDetail', params: { id: rowData.songId } }"
        >
          {{ rowData.song_title }}
        </router-link>
        <span v-else>{{ rowData.song_title }}</span>
      </template>
    </va-data-table>

    <h2 class="section-title">Misma canción, mismo usuario</h2>
    <va-data-table
      class="ekitable"
      :items="report ? report.repeated_pairs : []"
      :columns="repeatPairColumns"
      :sticky-header="true"
      no-data-html="<div class='no-data'>Sin datos para el rango elegido</div>"
    >
      <template #cell(song_title)="{ rowData }">
        <router-link
          v-if="rowData.songId"
          class="table-span linked"
          :to="{ name: 'songDetail', params: { id: rowData.songId } }"
        >
          {{ rowData.song_title }}
        </router-link>
        <span v-else>{{ rowData.song_title }}</span>
      </template>
    </va-data-table>
  </div>
</template>

<script>
import requester from "@/services/requester";

function currentMonthRange() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const lastDay = String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, "0");
  return {
    since: `${now.getFullYear()}-${month}-01`,
    until: `${now.getFullYear()}-${month}-${lastDay}`,
  };
}

export default {
  name: "PlaybackPatterns",
  data() {
    const month = currentMonthRange();
    return {
      since: month.since,
      until: month.until,
      isLoading: false,
      error: "",
      report: null,
      hourColumns: [
        { key: "hour_label", sortable: false, label: "Hora" },
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "unique_users", sortable: false, label: "Usuarios" },
        { key: "share", sortable: false, label: "Participación" },
      ],
      weekdayColumns: [
        { key: "weekday_name", sortable: false, label: "Día" },
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "unique_users", sortable: false, label: "Usuarios" },
        { key: "share", sortable: false, label: "Participación" },
      ],
      bucketColumns: [
        { key: "bucket", sortable: false, label: "Reproducciones por usuario" },
        { key: "users", sortable: false, label: "Usuarios" },
      ],
      userColumns: [
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "user_name", sortable: false, label: "Usuario" },
        { key: "unique_songs", sortable: false, label: "Canciones" },
        { key: "days_active", sortable: false, label: "Días activos" },
        { key: "plays_per_day", sortable: false, label: "Por día" },
      ],
      repeatSongColumns: [
        { key: "extra_plays", sortable: false, label: "Veces extra" },
        { key: "song_title", sortable: false, label: "Canción" },
        { key: "artist_name", sortable: false, label: "Artista" },
        { key: "repeat_users", sortable: false, label: "Usuarios que la repiten" },
        { key: "max_plays_one_user", sortable: false, label: "Máx. de un usuario" },
      ],
      repeatPairColumns: [
        { key: "plays", sortable: false, label: "Reproducciones" },
        { key: "user_name", sortable: false, label: "Usuario" },
        { key: "song_title", sortable: false, label: "Canción" },
        { key: "artist_name", sortable: false, label: "Artista" },
      ],
    };
  },
  methods: {
    formatNumber(value) {
      if (value == null || value === "") {
        return "-";
      }
      return Number(value).toFixed(1);
    },
    formatPercent(value) {
      if (value == null || value === "") {
        return "-";
      }
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    formatHour(hour) {
      if (hour == null || hour === "") {
        return "-";
      }
      const value = Number(hour);
      const period = value < 12 ? "a. m." : "p. m.";
      const display = value % 12 === 0 ? 12 : value % 12;
      return `${display}:00 ${period}`;
    },
    async loadReport() {
      this.isLoading = true;
      this.error = "";
      try {
        const query = [];
        if (this.since) {
          query.push(`since=${this.since}`);
        }
        if (this.until) {
          query.push(`until=${this.until}`);
        }
        const route = query.length ? `/patterns?${query.join("&")}` : "/patterns";
        const data = await requester.Get(route, true);
        if (data.status === 200 && data.data) {
          this.report = data.data;
        } else {
          this.error = "No se pudieron cargar los patrones.";
        }
      } catch (e) {
        console.log(e);
        this.error = "No se pudieron cargar los patrones.";
      }
      this.isLoading = false;
    },
  },
  created() {
    this.loadReport();
  },
};
</script>

<style lang="scss" scoped>
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  padding: 0 15px 20px;
}

.summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 0 15px 20px;
}

.summary-item {
  background: #123d62;
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.analytics-error,
.analytics-status {
  padding: 0 15px 16px;
}

.analytics-error {
  color: #f8b4b4;
}
</style>
